/**
 * Monta o payload "PIX copia e cola" (BR Code), no formato EMV®QRCPS-MPM
 * definido pelo Banco Central. É o mesmo texto que vira imagem de QR e que o
 * app do banco aceita colado.
 *
 * Estrutura: campos no formato ID (2 dígitos) + tamanho (2 dígitos) + valor,
 * terminando no CRC16-CCITT/FALSE dos bytes anteriores.
 */

const ID_PAYLOAD_FORMAT = "00";
const ID_MERCHANT_ACCOUNT = "26";
const ID_MERCHANT_ACCOUNT_GUI = "00";
const ID_MERCHANT_ACCOUNT_KEY = "01";
const ID_MERCHANT_CATEGORY_CODE = "52";
const ID_TRANSACTION_CURRENCY = "53";
const ID_TRANSACTION_AMOUNT = "54";
const ID_COUNTRY_CODE = "58";
const ID_MERCHANT_NAME = "59";
const ID_MERCHANT_CITY = "60";
const ID_ADDITIONAL_DATA = "62";
const ID_ADDITIONAL_DATA_TXID = "05";
const ID_CRC = "63";

const field = (id, value) => `${id}${String(value.length).padStart(2, "0")}${value}`;

/**
 * Acentos e símbolos fora do ASCII quebram a leitura em parte dos apps; nome e
 * cidade também têm limite de tamanho no padrão (25 e 15).
 */
const sanitize = (value, maxLength) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, maxLength);

/**
 * A chave vai no payload no formato canônico: CPF/CNPJ só dígitos, telefone
 * com +55, e-mail e aleatória como cadastrados.
 */
export function normalizeKeyForBrCode(keyType, key) {
  const _raw = String(key || "").trim();
  const _digits = _raw.replace(/\D/g, "");

  if (keyType === "cpf" || keyType === "cnpj") return _digits;
  if (keyType === "phone") return `+55${_digits.replace(/^55/, "")}`;
  if (keyType === "email") return _raw.toLowerCase();
  return _raw;
}

export function crc16(payload) {
  let _crc = 0xffff;
  for (let i = 0; i < payload.length; i++) {
    _crc ^= payload.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      _crc = _crc & 0x8000 ? (_crc << 1) ^ 0x1021 : _crc << 1;
      _crc &= 0xffff;
    }
  }
  return _crc.toString(16).toUpperCase().padStart(4, "0");
}

/**
 * @returns {string|null} o payload, ou null quando falta dado obrigatório
 * (chave, nome do recebedor ou cidade) — nesse caso não dá para gerar um QR
 * válido e a interface não deve mostrar um código que o banco vai recusar.
 */
export function buildPixPayload({ keyType, key, merchantName, merchantCity, amount, txid }) {
  const _key = normalizeKeyForBrCode(keyType, key);
  const _name = sanitize(merchantName, 25);
  const _city = sanitize(merchantCity, 15);
  if (!_key || !_name || !_city) return null;

  const _merchantAccount =
    field(ID_MERCHANT_ACCOUNT_GUI, "br.gov.bcb.pix") + field(ID_MERCHANT_ACCOUNT_KEY, _key);

  // "***" é o txid padrão para QR estático sem identificador próprio
  const _additionalData = field(ID_ADDITIONAL_DATA_TXID, sanitize(txid, 25) || "***");

  const _amount = amount > 0 ? Number(amount).toFixed(2) : null;

  const _payload =
    field(ID_PAYLOAD_FORMAT, "01") +
    field(ID_MERCHANT_ACCOUNT, _merchantAccount) +
    field(ID_MERCHANT_CATEGORY_CODE, "0000") +
    field(ID_TRANSACTION_CURRENCY, "986") +
    (_amount ? field(ID_TRANSACTION_AMOUNT, _amount) : "") +
    field(ID_COUNTRY_CODE, "BR") +
    field(ID_MERCHANT_NAME, _name) +
    field(ID_MERCHANT_CITY, _city) +
    field(ID_ADDITIONAL_DATA, _additionalData) +
    `${ID_CRC}04`;

  return _payload + crc16(_payload);
}
